import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { haeuserApi } from '../api/haeuser';
import SortTh from '../components/SortTh';
import Spinner from '../components/Spinner';
import EmptyTableRow from '../components/EmptyTableRow';
import Pagination from '../components/Pagination';
import { useSessionFilter } from '../hooks/useSessionFilter';
import { useColumnConfig, useModuleVisibility } from '../hooks/useConfigVisibility';

export default function HaeuserList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [searchText, setSearchText] = useSessionFilter<string>('haeuser_searchText', '');
  const [sortBy, setSortBy] = useSessionFilter<string>('haeuser_sortBy', 'adresse');
  const [sortDir, setSortDir] = useSessionFilter<'asc' | 'desc'>('haeuser_sortDir', 'asc');
  const cols = useColumnConfig('haeuser');
  const pv = useModuleVisibility();
  const perPage = 50;

  const { data, isLoading } = useQuery({
    queryKey: ['haeuser', { page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }],
    queryFn: () => haeuserApi.getHaeuser({ page, per_page: perPage, search_text: searchText || undefined, sort_by: sortBy, sort_dir: sortDir }),
  });

  const totalPages = data ? Math.ceil(data.total / perPage) : 0;

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="bg-white rounded-lg shadow p-4 mb-4">
          <input
            type="text"
            placeholder="Suche nach Bezeichnung, Baugenehmigung..."
            className="w-full border rounded-lg px-3 py-2 text-sm"
            value={searchText}
            onChange={(e) => { setSearchText(e.target.value); setPage(1); }}
          />
        </div>

        {isLoading ? (
          <Spinner />
        ) : (
          <>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <SortTh field="bezeichnung" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Bezeichnung</SortTh>
                    {cols.adresse !== false && pv.adressen !== false && <SortTh field="adresse" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>Adresse</SortTh>}
                    {cols.plz !== false && <SortTh field="plz" sortBy={sortBy} sortDir={sortDir} onSort={handleSort}>PLZ</SortTh>}
                    {cols.baugenehmigung !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Baugenehmigung</th>}
                    {cols.objekte !== false && pv.objekte !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Objekte</th>}
                    {cols.verfahren !== false && pv.verfahren !== false && <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase">Verfahren</th>}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {data?.items?.map((h) => (
                    <tr
                      key={h.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/haeuser/${h.id}`)}
                    >
                      <td className="px-3 py-3 text-sm font-medium text-gray-900">
                        {h.typ && <span className="text-xs text-gray-500 mr-1">{h.typ}</span>}
                        {h.bezeichnung || <span className="text-gray-400 italic">Ohne Bezeichnung</span>}
                      </td>
                      {cols.adresse !== false && pv.adressen !== false && (
                        <td className="px-3 py-3 text-sm">
                          <button
                            className="text-blue-600 hover:underline"
                            onClick={(e) => { e.stopPropagation(); navigate(`/adressen/${h.adresse_id}`); }}
                          >
                            {h.adresse_str}
                          </button>
                        </td>
                      )}
                      {cols.plz !== false && <td className="px-3 py-3 text-sm text-gray-500">{h.plz || '-'}</td>}
                      {cols.baugenehmigung !== false && <td className="px-3 py-3 text-sm text-gray-500">{h.baugenehmigung || '-'}</td>}
                      {cols.objekte !== false && pv.objekte !== false && (
                        <td className="px-3 py-3 text-sm">
                          {h.einheiten_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {h.einheiten_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                      {cols.verfahren !== false && pv.verfahren !== false && (
                        <td className="px-3 py-3 text-sm">
                          {h.verfahren_count > 0 ? (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-orange-100 text-orange-800">
                              {h.verfahren_count}
                            </span>
                          ) : (
                            <span className="text-gray-400">0</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                  {(!data?.items || data.items.length === 0) && (
                    <EmptyTableRow colSpan={6} message="Keine Häuser gefunden." />
                  )}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              totalPages={totalPages}
              total={data?.total ?? 0}
              entityLabel="Häuser"
              onPageChange={setPage}
            />
          </>
        )}
      </main>
    </div>
  );
}
